import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { DateTime } from 'luxon';
import { productUpdates as verifiedUpdates } from 'data/productUpdates';
import {
  GET_PRODUCT_UPDATES,
  ProductUpdate,
  ProductUpdatesData,
  ProductUpdatesVariables,
} from 'gql/productUpdates';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

const getDateWindow = (): ProductUpdatesVariables => {
  const now = DateTime.utc();
  return {
    fromDate: now.minus({ months: 6 }).toISO()!,
    throughDate: now.toISO()!,
  };
};

const schemaNotAvailable = (error?: ApolloError): boolean =>
  Boolean(
    error?.graphQLErrors?.some(
      ({ message, extensions }) =>
        ['validation-failed', 'GRAPHQL_VALIDATION_FAILED'].includes(
          String(extensions?.code),
        ) &&
        /(?:field ['"]productUpdates['"] not found|cannot query field ['"]productUpdates['"])/i.test(
          message,
        ),
    ),
  );

const fallbackUpdates: ProductUpdate[] = verifiedUpdates.map((update) => ({
  id: update.title,
  title: update.title,
  description: update.description,
  category: null,
  publishedAt: update.date,
}));

const linkClass =
  'rounded text-sm text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500';

export const ProductUpdates = () => {
  const [dateWindow, setDateWindow] = useState(getDateWindow);
  const lastRefreshAt = useRef(Date.parse(dateWindow.throughDate));
  const [showAll, setShowAll] = useState(false);
  const [lastSuccessfulUpdates, setLastSuccessfulUpdates] = useState<
    ProductUpdate[] | null
  >(null);
  const { data, loading, error, refetch } = useQuery<
    ProductUpdatesData,
    ProductUpdatesVariables
  >(GET_PRODUCT_UPDATES, {
    variables: dateWindow,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const liveUpdates = Array.isArray(data?.productUpdates)
    ? data!.productUpdates
    : null;

  useEffect(() => {
    if (liveUpdates) setLastSuccessfulUpdates(liveUpdates);
  }, [liveUpdates]);

  const refresh = useCallback(() => {
    lastRefreshAt.current = Date.now();
    const nextWindow = getDateWindow();
    if (nextWindow.throughDate === dateWindow.throughDate) {
      void refetch(nextWindow).catch(() => undefined);
    } else {
      setDateWindow(nextWindow);
    }
  }, [dateWindow.throughDate, refetch]);

  useEffect(() => {
    // Refresh the upper date bound too, so new releases can appear while Home stays open.
    const refreshIfStale = () => {
      if (Date.now() - lastRefreshAt.current >= REFRESH_INTERVAL_MS) refresh();
    };
    const interval = window.setInterval(refreshIfStale, REFRESH_INTERVAL_MS);
    window.addEventListener('focus', refreshIfStale);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshIfStale);
    };
  }, [refresh]);

  const usingFallback =
    !liveUpdates && !lastSuccessfulUpdates && schemaNotAvailable(error);
  const source =
    liveUpdates ||
    lastSuccessfulUpdates ||
    (usingFallback ? fallbackUpdates : null);
  const from = Date.parse(dateWindow.fromDate);
  const through = Date.parse(dateWindow.throughDate);
  const updates = (source || [])
    .filter((update) => {
      const published = Date.parse(update.publishedAt);
      return (
        Number.isFinite(published) && published >= from && published <= through
      );
    })
    .sort(
      (a, b) =>
        Date.parse(b.publishedAt) - Date.parse(a.publishedAt) ||
        a.id.localeCompare(b.id),
    )
    .slice(0, 100);
  const visibleUpdates = showAll ? updates : updates.slice(0, 3);
  const hasUpdates = updates.length > 0;
  const unavailable = !loading && (Boolean(error) || !source) && !hasUpdates;
  const moreCount = updates.length - 3;

  return (
    <section aria-label="Product updates" className="space-y-3">
      <h2 className="text-sm font-semibold text-gray-900">Product updates</h2>
      {loading && !hasUpdates && (
        <p role="status" className="text-sm text-gray-500">
          Loading product updates…
        </p>
      )}
      {usingFallback && hasUpdates && (
        <p className="text-xs text-gray-500">Previously verified updates</p>
      )}
      {error && hasUpdates && (
        <div className="space-y-1 text-xs text-gray-500">
          <p role="status">
            {usingFallback
              ? 'Live updates are temporarily unavailable.'
              : 'Showing previously loaded updates.'}
          </p>
          <button type="button" onClick={refresh} className={linkClass}>
            Retry updates
          </button>
        </div>
      )}
      {unavailable && (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-gray-600">
            Product updates could not be loaded.
          </p>
          <button type="button" onClick={refresh} className={linkClass}>
            Retry updates
          </button>
        </div>
      )}
      {!loading && !unavailable && !hasUpdates && (
        <p className="text-sm text-gray-500">
          No product updates in the last six months.
        </p>
      )}
      {hasUpdates && (
        <ul
          id="home-product-updates"
          className="space-y-4 text-sm text-gray-600"
        >
          {visibleUpdates.map(
            ({ id, title, publishedAt, description, category }) => (
              <li key={id}>
                <p className="font-medium text-gray-900">{title}</p>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <time dateTime={publishedAt}>
                    {new Date(publishedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      timeZone: 'UTC',
                    })}
                  </time>
                  {category && <span className="capitalize">{category}</span>}
                </div>
                <p className="mt-1">{description}</p>
              </li>
            ),
          )}
        </ul>
      )}
      {moreCount > 0 && (
        <button
          type="button"
          aria-expanded={showAll}
          aria-controls="home-product-updates"
          onClick={() => setShowAll(!showAll)}
          className={linkClass}
        >
          {showAll
            ? 'Show fewer updates'
            : `Show ${moreCount} more ${
                moreCount === 1 ? 'update' : 'updates'
              }`}
        </button>
      )}
    </section>
  );
};
